import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Navigation,
  Search,
  Filter,
  ShieldCheck,
  Star,
  Clock,
  Sparkles,
  Calendar,
  AlertCircle,
  CheckCircle2,
  User,
  Heart,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  LocateFixed,
  LayoutGrid,
  Map as MapIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  fetchNearbyCompanions,
  requestDeviceLocation,
  GeoLocationCoords,
  CompanionWithDistance,
} from '../lib/companionDiscoveryService';
import { Companion, BookingDetails } from '../types';
import { BookingConfirmationModal } from '../components/BookingConfirmationModal';
import { InteractiveGoogleMap } from '../components/location/InteractiveGoogleMap';
import { getCoordinatesForCity } from '../data/companionsData';
import { CompanionDetailModal } from '../components/CompanionDetailModal';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { isDemoCompanionProfile, getDemoCompanionBookingMessage } from '../data/companionsData';
import { useLanguage } from '../i18n/LanguageContext';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';

const POPULAR_CITIES = [
  'All Cities',
  'Kolkata',
  'Bardhaman',
  'Siliguri',
  'Durgapur',
  'Asansol',
  'Howrah',
  'Mumbai',
  'Delhi',
  'Bengaluru',
  'Pune',
];

const RADIUS_OPTIONS = [
  { label: '15 km (Local)', value: 15 },
  { label: '30 km (Nearby)', value: 30 },
  { label: '50 km (Metro Zone)', value: 50 },
  { label: '100 km (Regional)', value: 100 },
  { label: 'All Distances', value: 'all' as const },
];

export const ClientHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, currentUser, logout } = useAuth();
  const { language, t } = useLanguage();

  // Location discovery state
  const [userCoords, setUserCoords] = useState<GeoLocationCoords | null>(null);
  const [locationSource, setLocationSource] = useState<'gps' | 'manual' | 'default'>('default');
  const [selectedRadius, setSelectedRadius] = useState<number | 'all'>('all');
  const [selectedCity, setSelectedCity] = useState<string>('All Cities');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [locationMessage, setLocationMessage] = useState<string | null>(null);
  const [demoBookingNotice, setDemoBookingNotice] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');

  // Companions data state
  const [companions, setCompanions] = useState<CompanionWithDistance[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Modals state
  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [activeBooking, setActiveBooking] = useState<BookingDetails | null>(null);

  const clientId =
    (userProfile as any)?.clientId ||
    localStorage.getItem('pairup_client_id') ||
    'CL-MEMBER';

  const clientDisplayName =
    userProfile?.displayName || userProfile?.name || currentUser || 'Valued Client';

  // Fetch companions from Firestore
  const loadCompanions = async () => {
    setIsLoading(true);
    try {
      const data = await fetchNearbyCompanions({
        userCoords,
        radiusKm: selectedRadius,
        cityFilter: selectedCity === 'All Cities' ? undefined : selectedCity,
        genderFilter: selectedGender === 'all' ? undefined : selectedGender,
      });

      // Filter by text search
      let filtered = data;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            c.city.toLowerCase().includes(q) ||
            c.bio.toLowerCase().includes(q) ||
            c.supportedOccasions?.some((o) => o.toLowerCase().includes(q))
        );
      }

      setCompanions(filtered);
    } catch (err) {
      console.error('Failed to load companions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCompanions();
  }, [userCoords, selectedRadius, selectedCity, selectedGender]);

  // Handle GPS location request with clear device permissions
  const handleRequestGpsLocation = async () => {
    setIsLocating(true);
    setLocationMessage(null);
    try {
      const res = await requestDeviceLocation();
      setUserCoords(res.coords);
      setLocationSource('gps');
      setSelectedRadius(30); // Default to 30km radius once GPS is locked
      setLocationMessage('Device GPS location acquired! Showing nearest companions in order of real distance.');
    } catch (err: any) {
      setLocationMessage(err.message || 'Location permission denied. You can select your city manually below.');
      setLocationSource('manual');
    } finally {
      setIsLocating(false);
    }
  };

  const handleBookCompanion = (comp: Companion) => {
    if (isDemoCompanionProfile(comp)) {
      setDemoBookingNotice('Not available on this time / date');
      return;
    }
    const baseAmount = (comp.hourlyRate || 999) * 2;
    const taxes = Math.round(baseAmount * 0.18);
    const platformFee = 99;
    const totalAmount = baseAmount + taxes + platformFee;

    const defaultBooking: BookingDetails = {
      companion: comp,
      date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      time: '18:00',
      durationHours: 2,
      occasion: comp.supportedOccasions?.[0] || 'Dinner Date & Conversation',
      meetingLocation: `${comp.city} City Center / Verified Public Venue`,
      city: comp.city,
      baseAmount,
      taxes,
      platformFee,
      totalAmount,
    };
    setActiveBooking(defaultBooking);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col selection:bg-pink-500 selection:text-white">
      {/* Top Client Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-2xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5">
            <PairUpLogo className="w-10 h-10 shrink-0" />
            <div className="flex flex-col">
              <span className="text-xl font-black tracking-tight text-slate-900 leading-none">
                PairUp<span className="text-pink-600">Today</span>
              </span>
              <span className="text-[10px] font-bold text-slate-400">Verified Client Hub</span>
            </div>
          </Link>

          {/* Center Navigation Shortcuts */}
          <nav className="hidden md:flex items-center gap-1.5 text-xs font-bold text-slate-600">
            <Link
              to="/home"
              className="px-3 py-1.5 bg-pink-50 text-pink-600 rounded-full font-black flex items-center gap-1"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>Explore Companions</span>
            </Link>
            <Link
              to="/my-bookings"
              className="px-3 py-1.5 hover:bg-slate-100 rounded-full transition-colors flex items-center gap-1"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>My Bookings</span>
            </Link>
            <Link
              to="/my-account"
              className="px-3 py-1.5 hover:bg-slate-100 rounded-full transition-colors flex items-center gap-1"
            >
              <User className="w-3.5 h-3.5" />
              <span>My Account</span>
            </Link>
          </nav>

          {/* Right Profile & Client ID badge */}
          <div className="flex items-center gap-2.5">
            <LanguageSwitcher variant="compact" />
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-xs font-bold text-slate-900">{clientDisplayName}</span>
              <span className="text-[10px] font-mono font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                {clientId}
              </span>
            </div>

            <Link
              to="/my-account"
              className="w-9 h-9 rounded-full overflow-hidden border-2 border-pink-500 shadow-xs hover:scale-105 transition-transform"
              title="My Account"
            >
              <img
                src={
                  userProfile?.avatarUrl ||
                  'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80'
                }
                alt={clientDisplayName}
                className="w-full h-full object-cover"
              />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 w-full">
        {demoBookingNotice && (
          <div className="p-4 rounded-2xl bg-amber-50 border border-amber-300 text-amber-950 flex items-start justify-between gap-3 shadow-xs">
            <div className="space-y-1">
              <strong className="block text-xs font-black uppercase tracking-wider text-amber-800">
                Not available on this time / date
              </strong>
              <p className="text-xs font-semibold leading-relaxed">{demoBookingNotice}</p>
            </div>
            <button
              type="button"
              onClick={() => setDemoBookingNotice(null)}
              className="text-amber-700 hover:text-amber-950 text-xs font-bold px-2 py-1 rounded-lg cursor-pointer shrink-0"
            >
              ✕
            </button>
          </div>
        )}
        {/* Welcome Client Hero Banner */}
        <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10 space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-[11px] font-black uppercase tracking-wider text-pink-300 border border-white/10">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>Authenticated Client Session</span>
              <span className="font-mono text-white/80">({clientId})</span>
            </div>

            <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight">
              Welcome, {clientDisplayName}! 👋
            </h1>

            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Find verified, background-checked companion hosts near your location for dinners, cultural events, weddings, and city tours. All bookings are protected by PairUpToday Meetup Verification.
            </p>

            <div className="pt-2 flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={handleRequestGpsLocation}
                disabled={isLocating}
                className="px-4 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <LocateFixed className="w-4 h-4" />
                <span>{isLocating ? 'Acquiring GPS...' : 'Find Companions Near Me'}</span>
              </button>

              <Link
                to="/my-bookings"
                className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl border border-white/20 transition-all flex items-center gap-1.5"
              >
                <Calendar className="w-4 h-4 text-purple-300" />
                <span>My Bookings History</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Nearby Discovery Filter Bar */}
        <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-black text-slate-900 flex items-center gap-2">
                <Navigation className="w-4 h-4 text-pink-600" />
                <span>Nearby Companion Discovery</span>
                {locationSource === 'gps' && (
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full border border-emerald-200">
                    Live Device GPS
                  </span>
                )}
                {locationSource === 'manual' && (
                  <span className="text-[10px] bg-amber-100 text-amber-800 font-bold px-2 py-0.5 rounded-full border border-amber-200">
                    Manual City Selection
                  </span>
                )}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Real companion records retrieved directly from Cloud Firestore with accurate geographical distance.
              </p>
            </div>

            {/* Quick Status Message */}
            {locationMessage && (
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-xl flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 text-pink-600 shrink-0" />
                <span>{locationMessage}</span>
              </div>
            )}
          </div>

          {/* Filter Controls Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Search Input */}
            <div className="relative flex items-center">
              <input
                type="text"
                placeholder="Search name, bio, or occasion..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadCompanions()}
                className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
              />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
            </div>

            {/* Radius Selector */}
            <div>
              <select
                value={String(selectedRadius)}
                onChange={(e) => {
                  const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
                  setSelectedRadius(val);
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-pink-500 bg-white"
              >
                {RADIUS_OPTIONS.map((opt) => (
                  <option key={String(opt.value)} value={String(opt.value)}>
                    Radius: {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* City Selector */}
            <div>
              <select
                value={selectedCity}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setLocationSource('manual');
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-pink-500 bg-white"
              >
                {POPULAR_CITIES.map((c) => (
                  <option key={c} value={c}>
                    City: {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Gender Filter */}
            <div>
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-hidden focus:ring-2 focus:ring-pink-500 bg-white"
              >
                <option value="all">All Genders</option>
                <option value="female">Female Hosts</option>
                <option value="male">Male Hosts</option>
              </select>
            </div>
          </div>
        </div>

        {/* Companion Listings Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-lg font-black text-slate-900 tracking-tight">
              Available Approved Companions ({companions.length})
            </h3>
            <div className="flex items-center gap-2.5">
              <div className="inline-flex p-1 bg-white rounded-xl border border-slate-200 shadow-2xs">
                <button
                  type="button"
                  id="client-home-btn-grid"
                  onClick={() => setViewMode('grid')}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    viewMode === 'grid'
                      ? 'bg-slate-900 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Grid View"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  <span>Grid</span>
                </button>
                <button
                  type="button"
                  id="client-home-btn-map"
                  onClick={() => setViewMode('map')}
                  className={`flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    viewMode === 'map'
                      ? 'bg-pink-600 text-white shadow-2xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  title="Interactive Map View"
                >
                  <MapIcon className="w-3.5 h-3.5" />
                  <span>Map</span>
                </button>
              </div>

              <button
                type="button"
                onClick={loadCompanions}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1 cursor-pointer bg-slate-50 px-2.5 py-1.5 rounded-xl border border-slate-200"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="py-16 text-center space-y-3">
              <div className="w-8 h-8 border-3 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs font-bold text-slate-500">
                Retrieving real companion records and computing geographical distances...
              </p>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && companions.length === 0 && (
            <div className="p-12 text-center bg-white rounded-3xl border border-slate-200 space-y-3">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                <MapPin className="w-7 h-7" />
              </div>
              <h4 className="text-base font-black text-slate-900">
                No companions available in this radius
              </h4>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                We couldn't find any approved companion hosts within the selected radius or filters. Try expanding your search radius or choosing another city.
              </p>
              <div className="pt-2 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRadius('all');
                    setSelectedCity('All Cities');
                    setSelectedGender('all');
                    setSearchQuery('');
                  }}
                  className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
                >
                  View All Companions
                </button>
              </div>
            </div>
          )}

          {/* Real Companions Display (Grid vs Map View) */}
          {!isLoading && companions.length > 0 && (
            viewMode === 'map' ? (
              <div className="space-y-3">
                <InteractiveGoogleMap
                  companions={companions}
                  centerLocation={
                    userCoords
                      ? { latitude: userCoords.latitude, longitude: userCoords.longitude, label: 'My GPS Location' }
                      : (selectedCity !== 'All Cities' ? { ...getCoordinatesForCity(selectedCity), label: selectedCity } : null)
                  }
                  userCoords={userCoords}
                  onSelectCompanion={setSelectedCompanion}
                  onBookNow={handleBookCompanion}
                  height="500px"
                />
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {companions.map((comp) => (
                <div
                  key={comp.id}
                  className="bg-white rounded-3xl border border-slate-200 shadow-xs hover:shadow-md transition-all overflow-hidden flex flex-col justify-between group"
                >
                  {/* Photo & Distance Tag */}
                  <div className="relative aspect-4/3 overflow-hidden bg-slate-100">
                    <img
                      src={comp.image}
                      alt={comp.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />

                    {/* Distance Tag */}
                    {comp.distanceKm !== undefined && (
                      <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/70 backdrop-blur-md text-white text-[10px] font-black rounded-full flex items-center gap-1 shadow-sm">
                        <Navigation className="w-3 h-3 text-pink-400" />
                        <span>{comp.distanceKm} km away</span>
                      </div>
                    )}

                    {/* Verified Badge */}
                    {!isDemoCompanionProfile(comp) && comp.isVerified && (
                      <div className="absolute top-3 right-3 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center gap-0.5 shadow-sm">
                        <ShieldCheck className="w-3 h-3" />
                        <span>{t('companions.verifiedBadge')}</span>
                      </div>
                    )}

                    {/* Rate pill */}
                    <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-white/95 backdrop-blur-xs text-slate-900 text-xs font-black rounded-xl shadow-sm">
                      ₹{comp.hourlyRate?.toLocaleString('en-IN')}/hr
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="p-4 space-y-2.5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-black text-slate-900 tracking-tight">
                          {comp.name}
                        </h4>
                        <div className="flex items-center gap-1 text-[11px] font-bold text-amber-500">
                          <Star className="w-3.5 h-3.5 fill-current" />
                          <span>{comp.rating || 4.9}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-0.5">
                        <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                        <span>{comp.city}, {comp.state || 'India'}</span>
                      </div>

                      <p className="text-xs text-slate-600 line-clamp-2 mt-2 leading-relaxed">
                        {comp.bio}
                      </p>

                      {/* Occasions tags */}
                      {comp.supportedOccasions && comp.supportedOccasions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2.5">
                          {comp.supportedOccasions.slice(0, 2).map((occ, idx) => (
                            <span
                              key={idx}
                              className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md"
                            >
                              {occ}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-3 border-t border-slate-100 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedCompanion(comp)}
                        className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                      >
                        Profile Details
                      </button>

                      <button
                        type="button"
                        onClick={() => handleBookCompanion(comp)}
                        className={`flex-1 py-2 text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer flex items-center justify-center gap-1 ${
                          isDemoCompanionProfile(comp)
                            ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
                            : 'bg-pink-600 hover:bg-pink-700 text-white'
                        }`}
                      >
                        <span>{isDemoCompanionProfile(comp) ? 'Not available on this time / date' : t('companions.bookNow')}</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
        </div>
      </main>

      {/* Companion Detail Modal */}
      {selectedCompanion && (
        <CompanionDetailModal
          companion={selectedCompanion}
          onClose={() => setSelectedCompanion(null)}
          isWishlisted={false}
          onToggleWishlist={() => {}}
          onProceedToBooking={(details) => {
            setSelectedCompanion(null);
            if (isDemoCompanionProfile(details.companion)) {
              setDemoBookingNotice(getDemoCompanionBookingMessage(language));
              return;
            }
            setActiveBooking(details);
          }}
        />
      )}

      {/* Booking Confirmation Modal */}
      {activeBooking && (
        <BookingConfirmationModal
          booking={activeBooking}
          onClose={() => setActiveBooking(null)}
          onSuccess={() => {
            setActiveBooking(null);
            navigate('/my-bookings');
          }}
        />
      )}

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-slate-400 border-t border-slate-200 mt-10 bg-white">
        PairUpToday Client Portal • Real Firestore Geolocation Discovery
      </footer>
    </div>
  );
};

export default ClientHomePage;
